import { api } from './api';

// Login with email and password
export const login = async (email, password) => {
  try {
    const response = await api.post('/api/auth/login/', { email, password });
    // No token storage here - we only get the token after OTP verification
    return response.data;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

/**
 * Verify OTP sent to user's email
 * @param {string} email - User's email
 * @param {string} otp - One-time password received via email
 * @returns {Promise<Object>} - Response with token and user info
 */
export const verifyOtp = async (email, otp) => {
  try {
    const response = await api.post('/api/auth/verify-otp/', { email, otp });
    
    // Store authentication data
    localStorage.setItem('authToken', response.data.token);
    localStorage.setItem('user', JSON.stringify(response.data.user));
    localStorage.setItem('sessionStartTime', Date.now());
    
    // Set auth token for all future API requests
    api.defaults.headers.common['Authorization'] = `Token ${response.data.token}`;
    
    return response.data;
  } catch (error) {
    console.error('OTP verification error:', error);
    throw error;
  }
};

// Logout - clear localStorage
export const logout = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('user');
  localStorage.removeItem('sessionStartTime');
  // Clear provider PIN attempts on logout (unlocks access on next login)
  localStorage.removeItem('provider_pin_attempts');
  delete api.defaults.headers.common['Authorization'];
  // Redirect to login page can be handled by the component
};

// Check if user is authenticated
export const isAuthenticated = () => {
  return localStorage.getItem('authToken') !== null;
};

// Get user info
export const getUserInfo = () => {
  const userInfo = localStorage.getItem('user');
  return userInfo ? JSON.parse(userInfo) : null;
};

// Check if user is admin
export const isAdmin = () => {
  const userInfo = getUserInfo();
  return userInfo ? userInfo.is_superuser || userInfo.is_admin : false;
};

// Check if user is staff
export const isStaff = () => {
  const userInfo = getUserInfo();
  return userInfo ? userInfo.is_staff : false;
};

// Get access level (admin/staff/user)
export const getAccessLevel = () => {
  const userInfo = getUserInfo();
  if (!userInfo) return null;
  if (userInfo.is_superuser || userInfo.is_admin) return 'admin';
  if (userInfo.is_staff) return 'staff';
  return 'user';
};

// Check specific permissions
export const hasPermission = (permission) => {
  const userInfo = getUserInfo();
  if (!userInfo || !userInfo.permissions) return false;
  return userInfo.permissions[permission] === true;
};

// Common permission checks
export const canManageUsers = () => hasPermission('can_manage_users');
export const canManageOrders = () => hasPermission('can_manage_orders');
export const canViewDashboard = () => hasPermission('can_view_dashboard');
export const canManageMedicines = () => hasPermission('can_manage_medicines');
export const canManagePatients = () => hasPermission('can_manage_patients');

// Get auth token
export const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

// Initialize auth state from localStorage
export const initializeAuth = () => {
  const token = localStorage.getItem('authToken');
  if (token) {
    // Set auth token for all API requests
    api.defaults.headers.common['Authorization'] = `Token ${token}`;
    console.log('Auth token initialized from localStorage');
    return true;
  }
  return false;
};

// Set up session timeout (auto logout after inactivity)
export const setupSessionTimeout = (timeoutMinutes = 20160) => {
  let inactivityTimer;
  
  const resetTimer = () => {
    clearTimeout(inactivityTimer);
    inactivityTimer = setTimeout(() => {
      // Auto logout after inactivity
      logout();
      window.location.href = '/login';
    }, timeoutMinutes * 60 * 1000);
  };
  
  // Reset timer on user activity
  const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
  events.forEach(event => {
    document.addEventListener(event, resetTimer, true);
  });
  
  // Initial timer setup
  resetTimer();
  
  return () => {
    // Cleanup function
    events.forEach(event => {
      document.removeEventListener(event, resetTimer, true);
    });
    clearTimeout(inactivityTimer);
  };
};
